import React, { useEffect, useMemo, useState } from 'react';
import SingleSelect, { SingleSelectOption } from './SingleSelect';
import {
  FONT_OPTIONS,
  isFontAvailable,
  DEFAULT_FONT_FAMILY,
  DEFAULT_FONT_SIZE,
} from '../utils/fontOptions';
import {
  useDisplayPreferences,
  FONT_WEIGHT_KEYS,
  DEFAULT_FONT_WEIGHTS,
  MIN_FONT_WEIGHT,
  MAX_FONT_WEIGHT,
  type FontWeightKey,
  type FontWeights,
} from '../contexts/DisplayPreferencesContext';
import { MIN_FONT_PX, MAX_FONT_PX } from '../theme/resolve';

/** Value used by the "follow the site default" row — an empty stack means no override. */
export const SITE_DEFAULT_VALUE = '';

/**
 * Edits made since the last Save. A field that is absent (`undefined`) is
 * untouched and keeps following the saved value; `null` means the user has
 * explicitly cleared it back to the site default.
 */
interface FontDraft {
  family?: string | null;
  sizePx?: number | null;
  weights: Partial<Record<FontWeightKey, number | null>>;
}
const EMPTY_DRAFT: FontDraft = { weights: {} };

/** One sample line per weight step, so every slider has something to show. */
const SAMPLE_LINES: ReadonlyArray<{ key: FontWeightKey; text: string }> = [
  { key: 'normal', text: 'MUEHLSTEIN DOMESTIC 0000 — the quick brown fox' },
  { key: 'medium', text: 'Medium sample 1234567890' },
  { key: 'semibold', text: 'Semibold sample 1234567890' },
  { key: 'bold', text: 'Bold sample 1234567890' },
];

interface FontSliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  overridden: boolean;
  onChange: (value: number) => void;
  onReset: () => void;
}

const FontSlider: React.FC<FontSliderProps> = ({
  label, value, min, max, step = 1, suffix = '', overridden, onChange, onReset,
}) => (
  <div>
    <div className="flex items-center justify-between gap-3">
      <label className="text-sm text-label">{label}</label>
      <div className="flex items-center gap-2 flex-shrink-0">
        {overridden && (
          <button
            type="button"
            onClick={onReset}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            reset
          </button>
        )}
        <span className="text-xs text-muted tabular-nums w-12 text-right">{value}{suffix}</span>
      </div>
    </div>
    <input
      type="range"
      aria-label={label}
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="mt-1 w-full h-3 rounded-lg appearance-none cursor-pointer bg-theme-bg-tertiary"
    />
  </div>
);

/**
 * The "Font" section of Display Preferences: typeface, base text size and the
 * four weight steps, mirroring the Customize panel's Typography tab but as a
 * per-user preference (see the font block in DisplayPreferencesContext for why
 * these bypass the theme engine).
 *
 * Edits stage in a local draft and only reach the app on Save. Applying them
 * live made the controls move under the pointer — Base text size scales the
 * whole UI, so dragging that slider resized the slider being dragged — and it
 * fired a server write per slider tick. The sample box renders the draft, so
 * the user still sees the result before committing.
 *
 * Font availability is measured per machine, because several options ship with
 * Windows but not macOS — the dropdown must never claim to show a face the
 * browser would silently substitute. When detection can't run at all (no canvas
 * context), every option is offered rather than none: hiding the whole list
 * would be a worse failure than listing one face that falls back.
 */
const FontPreferencesSection: React.FC = () => {
  const { appFontFamily, appFontSizePx, appFontWeights, setAppFont } = useDisplayPreferences();

  const [draft, setDraft] = useState<FontDraft>(EMPTY_DRAFT);

  // What the app WOULD run after Save: the draft where touched, the saved value
  // elsewhere. Untouched fields therefore keep tracking the server load.
  const family = draft.family !== undefined ? draft.family : appFontFamily;
  const sizePx = draft.sizePx !== undefined ? draft.sizePx : appFontSizePx;
  const weights: FontWeights = {};
  for (const key of FONT_WEIGHT_KEYS) {
    const value = key in draft.weights ? draft.weights[key] : appFontWeights[key];
    if (value !== null && value !== undefined) weights[key] = value;
  }

  // Dirty means "differs from what is saved", so nudging a slider away and back
  // again leaves nothing to save.
  const isDirty =
    family !== appFontFamily ||
    sizePx !== appFontSizePx ||
    FONT_WEIGHT_KEYS.some((key) => weights[key] !== appFontWeights[key]);

  const setDraftWeight = (key: FontWeightKey, value: number | null) =>
    setDraft((d) => ({ ...d, weights: { ...d.weights, [key]: value } }));

  const handleSave = () => {
    setAppFont({ family, sizePx, weights });
    setDraft(EMPTY_DRAFT);
  };
  const handleCancel = () => setDraft(EMPTY_DRAFT);

  // Webfonts land asynchronously, so re-measure once document.fonts settles.
  const [fontsReady, setFontsReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    document.fonts?.ready?.then(() => { if (!cancelled) setFontsReady(true); });
    return () => { cancelled = true; };
  }, []);

  const options = useMemo<SingleSelectOption[]>(() => {
    const ctx = typeof document === 'undefined'
      ? null
      : document.createElement('canvas').getContext('2d');
    const canDetect = Boolean(ctx);

    const rows: SingleSelectOption[] = [
      { value: SITE_DEFAULT_VALUE, label: 'Site default' },
    ];
    let lastGroup = '';
    for (const font of FONT_OPTIONS) {
      if (canDetect && !isFontAvailable(font.primary, ctx)) continue;
      if (font.group !== lastGroup) {
        rows.push({ value: `__group_${font.group}`, label: font.group, isHeader: true });
        lastGroup = font.group;
      }
      rows.push({ value: font.stack, label: font.label });
    }
    return rows;
    // fontsReady is a deliberate trigger: it re-runs detection after webfonts load.
  }, [fontsReady]);

  // The sample is pinned to explicit values rather than inheriting the live CSS
  // vars: while a change is pending, the app is still running the OLD font, and
  // the sample has to show the NEW one — including "back to the site default"
  // while another face is still applied.
  const sampleStyle: React.CSSProperties = {
    fontFamily: family ?? DEFAULT_FONT_FAMILY,
    fontSize: `${sizePx ?? DEFAULT_FONT_SIZE}px`,
  };

  return (
    <div className="mt-4 pt-4 border-t border-theme-border-primary space-y-4">
      <div>
        <h4 className="text-sm font-semibold text-heading">Font</h4>
        <p className="text-xs text-muted mt-0.5">
          Applies to the whole app for your account only. Changes show in the sample below and
          take effect when you Save. Fonts your computer can't render are hidden, so the list
          never shows a face you wouldn't get.
        </p>
      </div>

      <div>
        <label className="text-sm text-label block mb-1">Typeface</label>
        <SingleSelect
          options={options}
          value={family ?? SITE_DEFAULT_VALUE}
          onChange={(value) =>
            setDraft((d) => ({ ...d, family: value === SITE_DEFAULT_VALUE ? null : value }))
          }
          ariaLabel="Typeface"
          enableSearch
          searchPlaceholder="Search fonts…"
          usePortal
        />
      </div>

      <FontSlider
        label="Base text size"
        value={sizePx ?? DEFAULT_FONT_SIZE}
        min={MIN_FONT_PX}
        max={MAX_FONT_PX}
        suffix="px"
        overridden={sizePx !== null}
        onChange={(v) => setDraft((d) => ({ ...d, sizePx: v }))}
        onReset={() => setDraft((d) => ({ ...d, sizePx: null }))}
      />
      <p className="text-xs text-muted -mt-2">
        Scales the whole interface, text and spacing together.
      </p>

      {FONT_WEIGHT_KEYS.map((key: FontWeightKey) => (
        <FontSlider
          key={key}
          label={`${key[0].toUpperCase()}${key.slice(1)} weight`}
          value={weights[key] ?? DEFAULT_FONT_WEIGHTS[key]}
          min={MIN_FONT_WEIGHT}
          max={MAX_FONT_WEIGHT}
          step={100}
          overridden={weights[key] !== undefined}
          onChange={(v) => setDraftWeight(key, v)}
          onReset={() => setDraftWeight(key, null)}
        />
      ))}
      <p className="text-xs text-muted">
        Weights outside 400-700 may render as a synthesised faux-bold on machines that do not
        have that cut of the typeface.
      </p>

      {/* Sample of the pending settings — weights are inline so the sample can
          show a draft weight while the app still runs the saved one. */}
      <div
        data-testid="font-preview"
        className="rounded-lg border border-theme-border-primary bg-theme-bg-tertiary px-3 py-2"
        style={sampleStyle}
      >
        {SAMPLE_LINES.map(({ key, text }) => (
          <div
            key={key}
            className="text-body"
            style={{ fontWeight: weights[key] ?? DEFAULT_FONT_WEIGHTS[key] }}
          >
            {text}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-end gap-2">
        {isDirty && (
          <span className="text-xs text-muted mr-auto">Unsaved changes</span>
        )}
        <button
          type="button"
          onClick={handleCancel}
          disabled={!isDirty}
          className="px-3 py-1.5 text-sm font-medium rounded-lg border border-theme-border-primary text-body hover:bg-theme-bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty}
          className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default FontPreferencesSection;
